<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="ns-form">
    <header class="ns-form__header">
      <button
        type="button"
        class="ns-form__back"
        aria-label="Torna al mese"
        @click="onCancel"
      >
        ‹
      </button>
      <h1>{{ expenseId ? 'Modifica spesa' : 'Nuova spesa' }}</h1>
    </header>

    <div v-if="loadState === 'loading'" class="ns-form__loading">
      Caricamento della spesa…
    </div>

    <div v-else-if="loadState === 'error'" class="ns-form__load-error">
      <p>{{ loadError }}</p>
      <button type="button" class="ns-btn ns-btn--secondary" @click="load">
        Riprova
      </button>
    </div>

    <form v-else class="ns-form__body" novalidate @submit.prevent="onSubmit">
      <p v-if="draftRestored" class="ns-form__note">
        Hai ripreso la spesa che non avevi salvato: la ricevuta, se c'era, va
        allegata di nuovo.
        <button type="button" @click="onDiscardDraft">Ricomincia</button>
      </p>

      <div class="ns-field">
        <label for="ns-expense-date">Data</label>
        <input id="ns-expense-date" v-model="form.date" type="date" required />
      </div>

      <div class="ns-field">
        <label for="ns-expense-category">Categoria</label>
        <select
          id="ns-expense-category"
          v-model="form.category"
          :class="{'ns-expense__placeholder': !form.category}"
          required
        >
          <option value="" disabled>Scegli la categoria</option>
          <option
            v-for="category in profile.categories"
            :key="category.code"
            :value="category.code"
          >
            {{ category.label }}
          </option>
        </select>
      </div>

      <div class="ns-field">
        <label for="ns-expense-amount">Importo</label>
        <div class="ns-expense__amount">
          <input
            id="ns-expense-amount"
            v-model="form.amount"
            type="text"
            inputmode="decimal"
            autocomplete="off"
            placeholder="0,00"
            maxlength="12"
            @blur="onAmountBlur"
          />
          <span aria-hidden="true">€</span>
        </div>
        <p v-if="amountInvalid" class="ns-field__invalid">
          Scrivi l'importo in euro, per esempio 12,50.
        </p>
      </div>

      <div class="ns-field">
        <label for="ns-expense-description">
          Descrizione <span class="ns-field__optional">(facoltativa)</span>
        </label>
        <textarea
          id="ns-expense-description"
          v-model="form.description"
          rows="2"
          maxlength="500"
          placeholder="Es. A4 Milano–Brescia, pranzo con il cliente"
        ></textarea>
      </div>

      <div class="ns-field">
        <span class="ns-field__label">
          Ricevuta <span class="ns-field__optional">(facoltativa)</span>
        </span>

        <div v-if="newReceipt" class="ns-receipt">
          <img
            v-if="newReceiptPreview"
            :src="newReceiptPreview"
            alt="Anteprima della ricevuta"
            class="ns-receipt__img"
          />
          <p v-else class="ns-receipt__file">📄 {{ newReceipt.name }}</p>
          <p class="ns-receipt__info">
            {{ newReceiptInfo }}
          </p>
          <button
            type="button"
            class="ns-btn ns-btn--ghost"
            @click="onDropNewReceipt"
          >
            Togli questa ricevuta
          </button>
        </div>

        <div v-else-if="hasSavedReceipt && !removeReceipt" class="ns-receipt">
          <img
            v-if="!savedReceiptIsFile"
            :src="savedReceiptHref"
            alt="Ricevuta salvata"
            class="ns-receipt__img"
            @error="savedReceiptIsFile = true"
          />
          <div class="ns-receipt__row">
            <a
              :href="savedReceiptHref"
              target="_blank"
              rel="noopener"
              class="ns-btn ns-btn--secondary ns-receipt__link"
            >
              Vedi ricevuta
            </a>
            <button
              type="button"
              class="ns-btn ns-btn--danger"
              @click="removeReceipt = true"
            >
              Rimuovi
            </button>
          </div>
        </div>

        <p v-else-if="removeReceipt && hasSavedReceipt" class="ns-form__note">
          La ricevuta verrà tolta al salvataggio.
          <button type="button" @click="removeReceipt = false">Annulla</button>
        </p>

        <p v-if="preparingReceipt" class="ns-receipt__info">Preparo la foto…</p>
        <p v-if="receiptError" class="ns-field__invalid" role="alert">
          {{ receiptError }}
        </p>

        <!--
          Due ingressi: con `capture` il telefono apre direttamente la
          fotocamera ma non lascia scegliere un PDF; senza, si sceglie fra
          galleria, file e fotocamera. Le etichette fanno da pulsante: il tocco
          apre il selettore del sistema senza passare da un click() in
          JavaScript, che su alcuni browser è bloccato.
        -->
        <div class="ns-receipt__pick">
          <label
            for="ns-receipt-camera"
            class="ns-btn ns-btn--secondary ns-receipt__pick-button"
          >
            📷 {{ hasAnyReceipt ? 'Rifai la foto' : 'Scatta la foto' }}
          </label>
          <label
            for="ns-receipt-file"
            class="ns-btn ns-btn--plain ns-receipt__pick-button"
          >
            Allega foto o PDF
          </label>
        </div>
        <input
          id="ns-receipt-camera"
          class="ns-visually-hidden"
          type="file"
          accept="image/*"
          capture="environment"
          :disabled="preparingReceipt"
          @change="onReceiptChosen"
        />
        <input
          id="ns-receipt-file"
          class="ns-visually-hidden"
          type="file"
          accept="image/*,application/pdf"
          :disabled="preparingReceipt"
          @change="onReceiptChosen"
        />
      </div>

      <p v-if="error" ref="error" class="ns-form__error" role="alert">
        {{ error }}
      </p>

      <div class="ns-form__actions">
        <button
          type="submit"
          class="ns-btn ns-btn--primary"
          :disabled="busy || preparingReceipt"
        >
          {{ saving ? 'Salvataggio…' : 'Salva spesa' }}
        </button>
        <button
          v-if="expenseId"
          type="button"
          class="ns-btn ns-btn--danger"
          :disabled="busy"
          @click="onDelete"
        >
          {{ deleting ? 'Eliminazione…' : 'Elimina' }}
        </button>
        <button type="button" class="ns-btn ns-btn--ghost" @click="onCancel">
          Annulla
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {nextTick} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {
  NOTE_SPESE_API_PATH,
  inlineErrorMessage,
} from '@/orangehrmNoteSpesePlugin/util/apiError';
import {
  clearDraft,
  loadDraft,
  saveDraft,
} from '@/orangehrmNoteSpesePlugin/util/draft';
import {normalizeAmount} from '@/orangehrmNoteSpesePlugin/util/money';
import {prepareReceipt} from '@/orangehrmNoteSpesePlugin/util/imageResize';

// Solo per una spesa nuova, come in TripForm. La ricevuta non va nella bozza
// (troppo grande per localStorage): la bozza salva solo i campi di testo.
const DRAFT_KEY = 'expense-new';

// "12.50" -> "12,50": nel campo l'importo si mostra all'italiana.
function displayAmount(decimal) {
  return decimal.replace('.', ',');
}

function sizeText(bytes) {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export default {
  name: 'ExpenseForm',

  props: {
    profile: {
      type: Object,
      required: true,
    },
    expenseId: {
      type: Number,
      default: null,
    },
    defaultDate: {
      type: String,
      required: true,
    },
  },

  emits: ['saved', 'cancel'],

  setup() {
    const expensesHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/expenses',
    );
    expensesHttp.setIgnorePath(NOTE_SPESE_API_PATH);

    return {expensesHttp};
  },

  data() {
    return {
      loadState: this.expenseId ? 'loading' : 'ready',
      loadError: '',
      form: null,
      initialJson: '',
      draftRestored: false,
      loadedDate: null,
      hasSavedReceipt: false,
      savedReceiptUrl: null,
      savedReceiptIsFile: false,
      removeReceipt: false,
      newReceipt: null,
      newReceiptPreview: null,
      preparingReceipt: false,
      receiptError: '',
      amountTouched: false,
      error: '',
      saving: false,
      deleting: false,
    };
  },

  computed: {
    busy() {
      return this.saving || this.deleting;
    },

    normalizedAmount() {
      return this.form ? normalizeAmount(this.form.amount) : null;
    },

    amountInvalid() {
      return (
        this.amountTouched &&
        this.form.amount.trim() !== '' &&
        this.normalizedAmount === null
      );
    },

    savedReceiptHref() {
      return window.appGlobal.baseUrl + this.savedReceiptUrl;
    },

    hasAnyReceipt() {
      return !!this.newReceipt || (this.hasSavedReceipt && !this.removeReceipt);
    },

    newReceiptInfo() {
      const kind = this.newReceiptPreview ? 'Foto ridotta' : 'PDF';
      return `${kind}, ${sizeText(
        this.newReceipt.size,
      )}: si carica al salvataggio.`;
    },

    isDirty() {
      return (
        !!this.form &&
        (JSON.stringify(this.form) !== this.initialJson ||
          !!this.newReceipt ||
          this.removeReceipt)
      );
    },
  },

  watch: {
    form: {
      deep: true,
      handler() {
        if (this.expenseId || this.loadState !== 'ready') return;
        if (JSON.stringify(this.form) !== this.initialJson) {
          saveDraft(DRAFT_KEY, this.form);
        } else {
          clearDraft(DRAFT_KEY);
        }
      },
    },
  },

  created() {
    this.load();
  },

  methods: {
    defaults() {
      return {
        date: this.defaultDate,
        category: '',
        amount: '',
        description: '',
      };
    },

    async load() {
      if (!this.expenseId) {
        const defaults = this.defaults();
        this.initialJson = JSON.stringify(defaults);
        const draft = loadDraft(DRAFT_KEY);
        this.form = draft ? this.fromDraft(draft, defaults) : defaults;
        this.draftRestored = !!draft;
        this.loadState = 'ready';
        return;
      }

      this.loadState = 'loading';
      try {
        const response = await this.expensesHttp.get(this.expenseId);
        const expense = response.data.data;
        this.form = {
          date: expense.date,
          category: expense.category,
          amount: displayAmount(String(expense.amount)),
          description: expense.description || '',
        };
        this.initialJson = JSON.stringify(this.form);
        this.loadedDate = expense.date;
        this.hasSavedReceipt = expense.hasReceipt;
        this.savedReceiptUrl = expense.receiptUrl;
        this.loadState = 'ready';
      } catch (error) {
        this.loadError =
          inlineErrorMessage(error) || 'Non riesco a caricare la spesa.';
        this.loadState = 'error';
      }
    },

    fromDraft(draft, defaults) {
      const form = {...defaults};
      Object.keys(defaults).forEach((key) => {
        if (typeof draft[key] === 'string') form[key] = draft[key];
      });
      if (!this.profile.categories.some((c) => c.code === form.category)) {
        form.category = '';
      }
      return form;
    },

    onDiscardDraft() {
      clearDraft(DRAFT_KEY);
      this.form = this.defaults();
      this.draftRestored = false;
      this.amountTouched = false;
      this.error = '';
    },

    onAmountBlur() {
      this.amountTouched = true;
      if (this.normalizedAmount !== null) {
        this.form.amount = displayAmount(this.normalizedAmount);
      }
    },

    async onReceiptChosen(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      // Svuotato subito: scegliere di nuovo lo stesso file deve funzionare.
      input.value = '';
      if (!file) return;

      this.preparingReceipt = true;
      this.receiptError = '';
      try {
        const receipt = await prepareReceipt(file);
        this.newReceipt = receipt;
        this.newReceiptPreview =
          receipt.type === 'application/pdf'
            ? null
            : `data:${receipt.type};base64,${receipt.base64}`;
      } catch (error) {
        this.receiptError =
          (error && error.message) ||
          'Non riesco a leggere questo file: riprova.';
      } finally {
        this.preparingReceipt = false;
      }
    },

    onDropNewReceipt() {
      this.newReceipt = null;
      this.newReceiptPreview = null;
      this.receiptError = '';
    },

    showError(message) {
      this.error = message;
      nextTick(() => {
        if (this.$refs.error) {
          this.$refs.error.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        }
      });
    },

    async onSubmit() {
      if (this.busy || this.preparingReceipt) return;
      this.amountTouched = true;

      const missing = [];
      if (!this.form.date) missing.push('data');
      if (!this.form.category) missing.push('categoria');
      if (this.normalizedAmount === null) missing.push('importo');
      if (missing.length > 0) {
        this.showError(`Da completare: ${missing.join(', ')}.`);
        return;
      }

      // Esattamente i parametri dichiarati da DirectorExpenseAPI. La
      // ricevuta si manda solo se è stata scelta adesso; removeReceipt solo
      // in modifica, e mai insieme a una ricevuta nuova (vincerebbe comunque
      // il contenuto, ma la richiesta resta leggibile).
      const body = {
        date: this.form.date,
        category: this.form.category,
        amount: this.normalizedAmount,
        description: this.form.description.trim() || null,
      };
      if (this.newReceipt) {
        const {name, type, base64, size} = this.newReceipt;
        body.receipt = {name, type, base64, size};
      }
      if (this.expenseId) {
        body.removeReceipt = this.removeReceipt && !this.newReceipt;
      }

      this.saving = true;
      this.error = '';
      try {
        const response = this.expenseId
          ? await this.expensesHttp.update(this.expenseId, body)
          : await this.expensesHttp.create(body);
        if (!this.expenseId) clearDraft(DRAFT_KEY);
        this.$emit('saved', {
          date: response.data.data.date,
          message: this.expenseId ? 'Spesa aggiornata.' : 'Spesa salvata.',
          placesChanged: false,
        });
      } catch (error) {
        // Il modulo resta com'è, ricevuta compresa: si corregge e si riprova.
        const message = inlineErrorMessage(error);
        if (message) this.showError(message);
      } finally {
        this.saving = false;
      }
    },

    async onDelete() {
      if (this.busy) return;
      if (!window.confirm('Eliminare questa spesa?')) return;

      this.deleting = true;
      this.error = '';
      try {
        await this.expensesHttp.delete(this.expenseId);
        this.$emit('saved', {
          date: this.loadedDate,
          message: 'Spesa eliminata.',
          placesChanged: false,
        });
      } catch (error) {
        const message = inlineErrorMessage(error);
        if (message) this.showError(message);
      } finally {
        this.deleting = false;
      }
    },

    onCancel() {
      if (
        this.isDirty &&
        !this.busy &&
        !window.confirm('Uscire senza salvare? Le modifiche andranno perse.')
      ) {
        return;
      }
      if (!this.expenseId) clearDraft(DRAFT_KEY);
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped src="./mobileForm.css"></style>

<style scoped>
.ns-field select.ns-expense__placeholder {
  color: var(--ns-muted);
}

.ns-expense__amount {
  position: relative;
}

.ns-expense__amount input {
  padding-right: 44px;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ns-expense__amount span {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  font-size: 20px;
  font-weight: 700;
  color: var(--ns-muted);
  pointer-events: none;
}

.ns-receipt {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1.5px solid #d7dae0;
}

.ns-receipt__img {
  display: block;
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 8px;
  background: #f2f3f5;
}

.ns-receipt__file {
  margin: 0;
  font-weight: 700;
  word-break: break-all;
}

.ns-receipt__info {
  margin: 0 0 8px;
  font-size: 14px;
  color: var(--ns-muted);
}

.ns-receipt__row {
  display: flex;
  gap: 10px;
}

.ns-receipt__row > * {
  flex: 1;
}

.ns-receipt__link {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.ns-receipt__pick {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ns-receipt__pick-button {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
}

/* Più specifico di `.ns-field input`, che altrimenti lo renderebbe visibile. */
.ns-field input.ns-visually-hidden {
  position: absolute;
  min-height: 0;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  border: 0;
}
</style>
